import { type HeadersFunction, json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { useLoaderData, type ShouldRevalidateFunction } from '@remix-run/react';
import React from 'react';

import { getFixedT } from '~/i18n.server';
import CategoryPage from '~/sections/category-page/CategoryPage';
import { CmsItem } from '~/types/Cms';
import { FilterDefinition } from '~/types/Filter';
import { Product } from '~/types/Product';
import { Taxon } from '~/types/Taxon';
import { apiFetch } from '~/utils/apiFetch';
import { buildBreadcrumbListJsonLd } from '~/utils/breadcrumbListJsonLd';
import { publicPageHeaders } from '~/utils/cacheControl.server';
import { buildSeoMeta, getRootOrigin, type ApiSeo, getShopName } from '~/utils/meta';
import { resolveRoutePath } from '~/utils/routePath.server';

interface TaxonDetails extends ApiSeo {
  id: number;
  name: string;
  description: CmsItem[] | null;
  code: string;
  level: number;
  parent?: string;
  banner?: {
    image: string;
    link: string | null;
  };
  slugTranslations: Record<string, string>;
  children: Taxon[];
}

export interface CategoryLoaderData {
  taxon: TaxonDetails | null;
  products: Product[];
  filters: FilterDefinition[];
  breadcrumbs: { name: string; slug: string }[];
  breadcrumbJsonLd: Record<string, unknown>;
  numberOfPages: number;
  hasMore: boolean;
  totalProductsCount: number;
  filteredProductsCount: number;
  API_URL: string;
}

const PER_PAGE = 24;

export async function loader({ params, request }: LoaderFunctionArgs): Promise<Response> {
  const API_URL = process.env.PUBLIC_API_URL;
  const taxonSlug = params['*'];

  if (!taxonSlug) {
    throw new Response('Category code missing', { status: 400 });
  }

  const url = new URL(request.url);
  const searchString = url.searchParams.toString();
  const page = Number(url.searchParams.get('page')) || 1;

  const fetchTaxon = async (): Promise<TaxonDetails | null> => {
    const res = await apiFetch(`${API_URL}/api/v2/shop/taxons-by-slug/${taxonSlug}`, {}, { request });
    if (!res.ok) return null;
    const data = await res.json();

    const childUrls: string[] = Array.isArray(data.children) ? data.children : [];

    const children: Taxon[] = (
      await Promise.all(
        childUrls.map(async (childUrl) => {
          const childRes = await apiFetch(
            `${API_URL}${childUrl}?imageFilter=sylius_shop_category_thumbnail`,
            {},
            { request }
          );
          if (!childRes.ok) return null;
          return childRes.json();
        })
      )
    ).filter((child): child is Taxon => child !== null);

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      code: data.code,
      level: data.level,
      slugTranslations: data.slugTranslations,
      // SEO block — this loader picks fields out one by one, so these have to be named
      // explicitly or `meta()` would never see them.
      metaTitle: data.metaTitle,
      metaDescription: data.metaDescription,
      metaKeywords: data.metaKeywords,
      ogTitle: data.ogTitle,
      ogDescription: data.ogDescription,
      ogImage: data.ogImage,
      ogImageWidth: data.ogImageWidth,
      ogImageHeight: data.ogImageHeight,
      parent: data.parent ? data.parent.split('/').pop() : undefined,
      // The shop API always returns a `banner` object, even when there is nothing to show
      // (disabled from admin, or no image/link configured on the taxon or in the
      // app.category_banner settings fallback) — in that case `image` is null. Only build a
      // banner here when there is an actual image, otherwise the FE would render a dangling
      // link with a broken `.../null` image src.
      banner: data.banner?.image
        ? {
            image: `${API_URL}${data.banner.image}`,
            link: data.banner.link ?? null,
          }
        : undefined,
      children,
    };
  };

  const taxon = await fetchTaxon();

  if (!taxon) {
    throw new Response('Category not found', { status: 404 });
  }

  const taxonCode = taxon.code;

  const fetchBreadcrumbs = async (): Promise<{ name: string; slug: string }[]> => {
    const res = await apiFetch(`${API_URL}/api/v2/shop/taxon-tree/${taxonCode}/path`, {}, { request });
    if (!res.ok) return [];
    const jsonData = await res.json();
    return (
      jsonData['hydra:member']?.map((t: { name: string; slug: string }) => ({
        name: t.name,
        slug: t.slug,
      })) ?? []
    );
  };

  const fetchProducts = async (): Promise<{
    products: Product[];
    numberOfPages: number;
    hasMore: boolean;
    filteredProductsCount: number;
  }> => {
    const queryParams = new URLSearchParams(searchString);
    queryParams.delete('page');
    const queryString = queryParams.toString();

    const productsUrl = `${API_URL}/api/v2/shop/products/search?limit=${PER_PAGE}&page=${page}&facets[taxon][]=${taxonCode}&imageFilter=sylius_shop_product_thumbnail${
      queryString ? `&${queryString}` : ''
    }`;
    const response = await apiFetch(productsUrl, {}, { request });
    const data = await response.json();

    return {
      products: data.products || [],
      numberOfPages: data.pagination?.total_pages || 1,
      hasMore: data.pagination?.has_next_page || false,
      filteredProductsCount: data.pagination?.total_items || 0,
    };
  };

  const fetchFilters = async (): Promise<{
    filters: FilterDefinition[];
    totalProductsCount: number;
  }> => {
    const queryParams = new URLSearchParams(searchString);
    queryParams.delete('page');
    const queryString = queryParams.toString();

    const res = await apiFetch(
      `${API_URL}/api/v2/shop/taxon-filters/${taxonCode}${queryString ? `?${queryString}` : ''}`,
      {},
      { request }
    );
    const jsonData = await res.json();

    return {
      filters: Object.values(jsonData.filters ?? {}),
      totalProductsCount: jsonData.taxonProducts || 0,
    };
  };

  const [breadcrumbs, productsData, filtersData] = await Promise.all([
    fetchBreadcrumbs(),
    fetchProducts(),
    fetchFilters(),
  ]);

  if (!taxon) {
    throw new Response('Category not found', { status: 404 });
  }

  // BreadcrumbList structured data, mirroring the on-page trail the section renders:
  // the home link followed by the category path. Resolved with the same locale and route
  // rules as the client's `useRoutePath`, and absolutised against the request origin.
  const { routePath } = resolveRoutePath(request);
  const t = await getFixedT(request);
  const breadcrumbJsonLd = buildBreadcrumbListJsonLd(
    [
      { name: t('common.home'), url: routePath('homepage') },
      ...breadcrumbs.map((crumb) => ({ name: crumb.name, url: routePath('category', crumb.slug) })),
    ],
    url.origin
  );

  return json<CategoryLoaderData>({
    taxon,
    products: productsData.products,
    filters: filtersData.filters,
    breadcrumbs,
    breadcrumbJsonLd,
    numberOfPages: productsData.numberOfPages,
    hasMore: productsData.hasMore,
    totalProductsCount: filtersData.totalProductsCount,
    filteredProductsCount: productsData.filteredProductsCount,
    API_URL: API_URL || '',
  });
}

export const headers: HeadersFunction = ({ parentHeaders }) => publicPageHeaders(parentHeaders);

export const meta: MetaFunction<typeof loader> = ({ data, matches, location }) => {
  const taxon = data?.taxon;
  // Stand-in until the backend computes `metaDescription` for taxons — it sends null
  // for all of them today, so without this categories would ship no description.
  const descText = (() => {
    if (!taxon?.description || !Array.isArray(taxon.description)) return '';
    const html = taxon.description.find((i: { code: string }) => i.code === 'monsieurbiz.html') as
      | { code: string; data: { content: string } }
      | undefined;
    return html?.data?.content?.replace(/<[^>]*>/g, '').trim() || '';
  })();

  return buildSeoMeta({
    seo: taxon,
    fallbackTitle: taxon?.name || 'Kategorie',
    fallbackDescription: descText,
    origin: getRootOrigin(matches),
    shopName: getShopName(matches),
    path: location.pathname,
  });
};

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentUrl, nextUrl }) => {
  return currentUrl.pathname !== nextUrl.pathname;
};

const CategoryPageRoute: React.FC = () => {
  const data = useLoaderData<typeof loader>() as CategoryLoaderData;
  return (
    <>
      <script
        type="application/ld+json"
        // Escape "<" so category names can never close the script element early.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data.breadcrumbJsonLd).replace(/</g, '\\u003c') }}
      />
      <CategoryPage serverData={data} />
    </>
  );
};

export default CategoryPageRoute;
